import { useMemo, useState } from 'react'
import { ArrowUpDown, ListFilter } from 'lucide-react'
import { toast } from 'sonner'
import { BookReservationItem } from '@/components/BookReservationItem'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuth } from '@/hooks/useAuth'
import { useFriends } from '@/hooks/useFriends'
import { useLoans } from '@/hooks/useLoans'

type ReservationFilter = 'all' | 'on-loan' | 'borrowed' | 'returned'
type ReservationSort = 'name' | 'author' | 'last-action'

interface ReservationListItem {
  key: string
  loanId: number
  friendId: number
  title: string
  thumbnailUrl: string | null
  author: string
  status: 'on-loan' | 'borrowed' | 'returned'
  lastAction: string
  lastActionAt: number
}

const formatActionDate = (value: string | null) => {
  if (!value) {
    return '-'
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(value))
}

export const BookedOut = () => {
  const { user } = useAuth()
  const { friends } = useFriends(user?.id)
  const { activeLoans, historyLoans, loading, error, returnLoan } = useLoans(user?.id)
  const [sortBy, setSortBy] = useState<ReservationSort>('last-action')
  const [filterBy, setFilterBy] = useState<ReservationFilter>('all')
  const [friendFilterId, setFriendFilterId] = useState('all-friends')

  const reservationItems = useMemo<ReservationListItem[]>(() => {
    const activeItems = activeLoans.map((loan) => {
      const loanedAt = new Date(loan.loaned_at).getTime()
      const actionLabel =
        loan.loan_type === 'on-loan'
          ? `Loaned to ${loan.friend.name} on ${formatActionDate(loan.loaned_at)}`
          : `Borrowed from ${loan.friend.name} on ${formatActionDate(loan.loaned_at)}`

      return {
        key: `active-${loan.id}`,
        loanId: loan.id,
        friendId: loan.friend.id,
        title: loan.book.title,
        thumbnailUrl: loan.book.cover_url,
        author: loan.book.authors[0] ?? 'Unknown author',
        status: loan.loan_type,
        lastAction: actionLabel,
        lastActionAt: loanedAt,
      }
    })

    const returnedItems = historyLoans.map((loan) => {
      const returnedAt = loan.returned_at ? new Date(loan.returned_at).getTime() : 0
      const returnLabel =
        loan.loan_type === 'on-loan'
          ? `Returned by ${loan.friend.name} on ${formatActionDate(loan.returned_at)}`
          : `Returned to ${loan.friend.name} on ${formatActionDate(loan.returned_at)}`

      return {
        key: `returned-${loan.id}`,
        loanId: loan.id,
        friendId: loan.friend.id,
        title: loan.book.title,
        thumbnailUrl: loan.book.cover_url,
        author: loan.book.authors[0] ?? 'Unknown author',
        status: 'returned' as const,
        lastAction: returnLabel,
        lastActionAt: returnedAt,
      }
    })

    return [...activeItems, ...returnedItems]
  }, [activeLoans, historyLoans])

  const filteredAndSortedItems = useMemo(() => {
    const filtered = reservationItems.filter((item) => {
      const matchesStatus = filterBy === 'all' ? true : item.status === filterBy
      const matchesFriend = friendFilterId === 'all-friends' ? true : String(item.friendId) === friendFilterId

      return matchesStatus && matchesFriend
    })

    return filtered.sort((left, right) => {
      if (sortBy === 'name') {
        return left.title.localeCompare(right.title)
      }

      if (sortBy === 'author') {
        return left.author.localeCompare(right.author)
      }

      return right.lastActionAt - left.lastActionAt
    })
  }, [filterBy, friendFilterId, reservationItems, sortBy])

  const handleReturn = async (loanId: number) => {
    const { error: returnError } = await returnLoan(loanId)

    if (returnError) {
      toast.error(returnError.message)
      return
    }

    toast.success('Book marked as returned')
  }

  return (
    <main className='mx-auto w-full max-w-5xl p-3 md:p-6'>
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between gap-3'>
            <div>
              <CardTitle>Bookings</CardTitle>
              <CardDescription>Where are my books?</CardDescription>
            </div>
            <div className='flex flex-wrap items-center justify-end gap-2'>
              <Select value={sortBy} onValueChange={(value) => setSortBy(value as ReservationSort)}>
                <SelectTrigger className='h-8 w-auto min-w-0 gap-1 px-2 text-xs' aria-label='Sort books'>
                  <ArrowUpDown className='h-3.5 w-3.5' />
                  <span className='hidden sm:inline'>Sort</span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='author'>Author</SelectItem>
                  <SelectItem value='name'>Name</SelectItem>
                  <SelectItem value='last-action'>Date of last action</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterBy} onValueChange={(value) => setFilterBy(value as ReservationFilter)}>
                <SelectTrigger className='h-8 w-auto min-w-0 gap-1 px-2 text-xs' aria-label='Filter books'>
                  <ListFilter className='h-3.5 w-3.5' />
                  <span className='hidden sm:inline'>Filter</span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>All</SelectItem>
                  <SelectItem value='on-loan'>On Loan</SelectItem>
                  <SelectItem value='borrowed'>Borrowed</SelectItem>
                  <SelectItem value='returned'>Returned</SelectItem>
                </SelectContent>
              </Select>

              <Select value={friendFilterId} onValueChange={setFriendFilterId}>
                <SelectTrigger className='h-8 min-w-[140px] text-xs' aria-label='Filter books by friend'>
                  <SelectValue placeholder='Select friend' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all-friends'>Everyone</SelectItem>
                  {friends.map((friend) => (
                    <SelectItem key={friend.id} value={String(friend.id)}>
                      {friend.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className='space-y-3'>
          {loading && <p className='text-sm text-muted-foreground'>Loading...</p>}
          {error && <p className='text-sm text-destructive'>{error}</p>}
          {!loading && filteredAndSortedItems.length === 0 && (
            <p className='text-sm text-muted-foreground'>No books match this filter.</p>
          )}
          {filteredAndSortedItems.map((item) => (
            <BookReservationItem
              key={item.key}
              title={item.title}
              thumbnailUrl={item.thumbnailUrl}
              author={item.author}
              status={item.status}
              lastAction={item.lastAction}
              action={
                item.status !== 'returned' ? (
                  <Button variant='default' size='sm' className='w-full sm:w-auto' onClick={() => void handleReturn(item.loanId)}>
                    Return Book
                  </Button>
                ) : undefined
              }
            />
          ))}
        </CardContent>
      </Card>
    </main>
  )
}
